import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import API from "../../../util/API";
import { addToCart, checkout } from "../../../features/cartSlice";
import USER_PATHS from "../USER_PATHS";
import { formatMoney as fmtMoney, normalizeCurrencyRates } from "../../../util/money";
import Preloader from "../../../components/Preloader";
import { buildProductPath } from "../../../util/productSlug";
import { selectSkuForOption, skuAttributes } from "../../../util/productVariants";

const isDigitsOnly = (value) => /^\d+$/.test(String(value ?? "").trim());

const pickReadableValue = (candidates) => {
  const cleaned = (candidates || [])
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter(Boolean);
  if (!cleaned.length) return "";
  const nonNumeric = cleaned.find((v) => !isDigitsOnly(v));
  return nonNumeric || cleaned[0];
};

const htmlToTextLines = (input) => {
  const raw = String(input ?? "");
  if (!raw.trim()) return [];

  // Fast path: already plain text.
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);

  let text = raw;
  if (looksLikeHtml && typeof window !== "undefined" && typeof window.DOMParser !== "undefined") {
    // Preserve basic breaks before parsing to text.
    const withBreaks = raw
      .replace(/<\s*br\s*\/?\s*>/gi, "\n")
      .replace(/<\s*\/\s*p\s*>/gi, "\n")
      .replace(/<\s*p\b[^>]*>/gi, "");
    const doc = new window.DOMParser().parseFromString(withBreaks, "text/html");
    // Prevent script/style content from leaking into textContent.
    doc.querySelectorAll("script,style,noscript").forEach((el) => el.remove());
    text = doc.body?.textContent || "";
  } else if (looksLikeHtml) {
    // Fallback: very basic tag stripping.
    text = raw.replace(/<[^>]+>/g, " ");
  }

  const normalized = text
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");

  // Split either by newlines or bullet char if it’s a single long line.
  const baseLines = normalized.includes("\n")
    ? normalized.split("\n")
    : normalized.split(/\s*[•\u2022]\s*/g);

  return baseLines
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter(Boolean);
};

const toNumber = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const s = String(value)
    .replace(/,/g, "")
    .replace(/[^0-9.+-]/g, "")
    .trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const getSkuStock = (sku) => toNumber(sku?.sku_available_stock) ?? 0;
const isSkuInStock = (sku) => getSkuStock(sku) > 0;

const QuickViewModal = ({ productData, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedCurrency = useSelector((state) => state?.userSettings?.currency) || "USD";
  const shipToCountry = useSelector((state) => state?.userSettings?.shipToCountry) || "US";
  const currencyPayload = useSelector((state) => state?.currency?.currency_list) || null;
  const rates = normalizeCurrencyRates(currencyPayload);

  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [selectedSKU, setSelectedSKU] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isOrdering, setIsOrdering] = useState(false);


  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);


  useEffect(() => {
    if (!productData?.id) return;
    let alive = true;

    (async () => {
      setIsLoading(true);
      setFetchError(null);
      setDetails(null);
      setSelectedSKU(null);
      try {
        const resp = await API.get(
          `/product/${productData.id}?country=${encodeURIComponent(shipToCountry)}&currency=${encodeURIComponent(
            selectedCurrency
          )}`
        );
        const payload = resp?.data;
        const resolved = payload?.data ?? payload;
        if (!alive) return;
        setDetails(resolved || null);
      } catch (err) {
        if (!alive) return;
        setDetails(null);
        setFetchError("Failed to load product details.");
      } finally {
        if (!alive) return;
        setIsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [productData?.id, shipToCountry, selectedCurrency]);

  useEffect(() => {
    if (!details?.skus?.length) return;

    const hasSkuImage = (sku) =>
      sku?.ae_sku_property_dtos?.ae_sku_property_d_t_o?.some((p) => p?.sku_image);

    const firstSku =
      details.skus.find((sku) => isSkuInStock(sku) && hasSkuImage(sku)) ||
      details.skus.find(isSkuInStock) ||
      details.skus.find(hasSkuImage) ||
      details.skus[0];

    if (!firstSku) return;

    const attrs = {};
    firstSku?.ae_sku_property_dtos?.ae_sku_property_d_t_o?.forEach((prop) => {
      attrs[prop.sku_property_name] = pickReadableValue([
        prop?.property_value_definition_name,
        prop?.sku_property_value,
      ]);
    });
    setSelectedAttributes(attrs);
    setSelectedSKU(firstSku);
    const imgProp = firstSku?.ae_sku_property_dtos?.ae_sku_property_d_t_o?.find((p) => p?.sku_image);
    setSelectedImage(imgProp?.sku_image || details?.images?.[0] || null);
  }, [details]);

  useEffect(() => {
    if (!details?.skus?.length) return;
    if (!selectedAttributes || !Object.keys(selectedAttributes).length) return;

    const foundSku = details.skus.find((sku) =>
      Object.keys(selectedAttributes).every((attrName) =>
        sku?.ae_sku_property_dtos?.ae_sku_property_d_t_o?.some((prop) => {
          const val = pickReadableValue([
            prop?.property_value_definition_name,
            prop?.sku_property_value,
          ]);
          return prop.sku_property_name === attrName && val === selectedAttributes[attrName];
        })
      )
    );

    if (foundSku) {
      setSelectedSKU(foundSku);
      const img = foundSku?.ae_sku_property_dtos?.ae_sku_property_d_t_o?.find((p) => p?.sku_image);
      setSelectedImage(img?.sku_image || details?.images?.[0] || null);
    } else {
      setSelectedSKU(null);
    }
  }, [selectedAttributes, details]);

  const availableOptions = useMemo(() => {
    const options = {};
    const skus = details?.skus;
    if (!Array.isArray(skus)) return options;
    skus.forEach((sku) => {
      sku?.ae_sku_property_dtos?.ae_sku_property_d_t_o?.forEach((prop) => {
        const propName = prop.sku_property_name;
        const propValue = pickReadableValue([
          prop?.property_value_definition_name,
          prop?.sku_property_value,
        ]);
        if (!propName || !propValue) return;
        if (!options[propName]) options[propName] = {};
        if (!options[propName][propValue]) {
          options[propName][propValue] = {
            value: propValue,
            image: prop.sku_image || null,
          };
        }
      });
    });
    return options;
  }, [details]);

  const hasProduct = Boolean(productData);
  const merged = details || productData || {};
  const productKey = productData?.id || merged?._id || merged?.id;
  const publicId = merged?.publicId || merged?._id || productData?.publicId || productData?._id || "";

  const name = merged?.name || productData?.name || "";
  const shortDescription = merged?.shortDescription || merged?.description || productData?.shortDescription || "";
  const productId = merged?.productId || productData?.productId || productData?.id;

  const productCurrency = (() => {
    const c = String(
      merged?.currency || merged?.currency_code || merged?.price_currency || productData?.currency || "USD"
    ).toUpperCase();
    return /^[A-Z]{3}$/.test(c) ? c : "USD";
  })();

  const displayPrice =
    toNumber(selectedSKU?.sell_price ?? selectedSKU?.offer_sale_price) ??
    toNumber(merged?.price) ??
    toNumber(productData?.price) ??
    0;

  const stock =
    toNumber(selectedSKU?.sku_available_stock) ??
    toNumber(merged?.availableStock) ??
    toNumber(productData?.availableStock) ??
    toNumber(merged?.stock) ??
    toNumber(productData?.stock) ??
    0;

  const imageUrl =
    selectedImage ||
    productData?.imageUrl ||
    (Array.isArray(merged?.images) && merged.images.length ? merged.images[0] : null);

  const descriptionLines = useMemo(() => {
    const lines = htmlToTextLines(shortDescription);
    if (!lines.length) return [];

    // Remove common logistics/shipping lines so quick view focuses on the product.
    const filtered = lines.filter((l) => !/(shipping|delivery|logistic|logistics|estimated|eta|arriv)/i.test(l));
    const finalLines = filtered.length ? filtered : lines;

    // Keep it compact for quick view.
    return finalLines.slice(0, 4);
  }, [shortDescription]);

  const variantLabel = (() => {
    if (!selectedSKU) return "";
    const props = selectedSKU?.ae_sku_property_dtos?.ae_sku_property_d_t_o;
    if (!Array.isArray(props) || !props.length) return "";
    return props
      .map((p) => {
        const v = pickReadableValue([
          p?.property_value_definition_name,
          p?.sku_property_value,
        ]);
        return p.sku_property_name && v ? `${p.sku_property_name}: ${v}` : null;
      })
      .filter(Boolean)
      .join(" • ");
  })();

  const requiresSku = Array.isArray(details?.skus) && details.skus.length > 0;
  const hasSkuSelected = requiresSku && Boolean(selectedSKU?.sku_id);
  const isInStock = stock > 0;
  const minimumQuantity = Math.max(1, Math.trunc(Number(selectedSKU?.min_order_quantity || selectedSKU?.minOrderQuantity || selectedSKU?.sku_bulk_order || 1)) || 1);
  const canPurchase = Boolean(details) && !fetchError && hasSkuSelected && isInStock && stock >= minimumQuantity && displayPrice > 0 && !isLoading;

  const buildCartPayload = () => {
    const skuId = selectedSKU?.sku_id;
    const baseId = String(productId || productKey || "");
    const cartId = skuId ? `${baseId}:${skuId}` : baseId;
    return {
      id: cartId,
      product_id: productId,
      publicId,
      name,
      price: displayPrice,
      quantity: minimumQuantity,
      min_order_quantity: minimumQuantity,
      image: imageUrl || "/placeholder.svg",
      imageUrl: imageUrl || "/placeholder.svg",
      sku_id: skuId,
      currency: selectedCurrency || productCurrency || "USD",
      variant: variantLabel || undefined,
    };
  };

  const handleAddToCartClick = () => {
    if (!canPurchase || isAdding) return;
    setIsAdding(true);
    try {
      dispatch(addToCart(buildCartPayload()));
      onClose();
    } finally {
      setIsAdding(false);
    }
  };

  const handleBuyNowClick = () => {
    if (!canPurchase || isOrdering) return;
    setIsOrdering(true);
    try {
      dispatch(checkout([buildCartPayload()]));
      navigate(USER_PATHS.SHIPPING);
      onClose();
    } finally {
      setIsOrdering(false);
    }
  };

  if (!hasProduct) return null;

  const showFullLoader = isLoading && !details;

  const modal = (
    <Dialog open={hasProduct} onClose={onClose}
      className="fixed inset-0 z-[9999] bg-slate-900/60 p-3 sm:p-6 grid place-items-center"
    >
      <DialogPanel
        className="w-full max-w-4xl h-[min(82vh,calc(100vh-3rem))] bg-white rounded-3xl border border-slate-200 shadow-2xl relative overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
          <DialogTitle className="sr-only">Choose options for {name}</DialogTitle>
          {/* Fixed header (keeps close button out of scroll areas) */}
          <div className="flex-shrink-0 h-14 px-3 sm:px-5 flex items-center justify-end bg-white border-b border-slate-100">
            <button
              onClick={onClose}
              className="group h-10 w-10 rounded-xl border border-slate-200 bg-white text-slate-500 inline-flex items-center justify-center transition-all hover:text-[#D2042D] hover:border-[#D2042D]/40 hover:bg-rose-50"
              aria-label="Close quick view"
            >
              <span className="text-3xl leading-none transition-transform group-hover:scale-110">&times;</span>
            </button>
          </div>

          <div className={"min-h-0 flex-1 overflow-y-auto " + (showFullLoader ? "p-0" : "p-5 sm:p-6")}> 
            {showFullLoader ? (
              <div className="h-full grid place-items-center">
                <Preloader variant="inline" />
              </div>
            ) : (
              <div className="h-full flex flex-col md:flex-row gap-6 md:overflow-hidden">
                {/* Left column (fixed on desktop) */}
                <div className="md:w-1/2 md:flex md:flex-col md:pr-2">
                  {imageUrl ? (
                    <div className="w-full aspect-square rounded-2xl border border-slate-200 bg-white flex items-center justify-center overflow-hidden">
                      <img src={imageUrl} alt={name} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-full aspect-square rounded-2xl border border-slate-200 bg-white flex items-center justify-center text-slate-500 text-sm">
                      No image
                    </div>
                  )}

                  {/* Desktop actions under image */}
                  <div className="hidden md:block mt-4">
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleAddToCartClick}
                        className="w-full h-10 px-4 rounded-xl text-sm font-semibold bg-slate-900 text-white transition-colors hover:bg-[#D2042D] disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
                        disabled={!canPurchase || isAdding}
                      >
                        {isAdding ? "Adding…" : "Add to Cart"}
                      </button>

                      <button
                        onClick={handleBuyNowClick}
                        className="w-full h-10 px-4 rounded-xl text-sm font-semibold bg-[#D2042D] text-white transition-colors hover:bg-[#C1032A] disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
                        disabled={!canPurchase || isOrdering}
                      >
                        {isOrdering ? "Processing…" : "Buy Now"}
                      </button>

                      {productKey ? (
                        <Link
                          to={buildProductPath(
                            { publicId, productId, name },
                            name,
                            selectedSKU?.sku_id ? `sku=${encodeURIComponent(String(selectedSKU.sku_id))}` : ""
                          )}
                          onClick={onClose}
                          className="block"
                        >
                          <span className="w-full h-10 px-4 rounded-xl text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-center">View full product details</span>
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Right column (scrollable on desktop) */}
                <div className="md:w-1/2 flex flex-col md:h-full md:pr-1">
                <h3 className="text-2xl font-semibold text-slate-900">{name}</h3>

                <div className="mt-2 flex items-baseline gap-3">
                  <p className="text-[#D2042D] text-3xl font-black">
                    {fmtMoney(displayPrice, { from: productCurrency, to: selectedCurrency, rates })}
                  </p>
                  {variantLabel ? (
                    <span className="text-xs font-semibold text-slate-500">{variantLabel}</span>
                  ) : null}
                </div>

                {isLoading ? (
                  <p className="mt-3 text-sm text-gray-500">Loading variants…</p>
                ) : fetchError ? (
                  <p className="mt-3 text-sm text-red-600">{fetchError}</p>
                ) : null}

                {requiresSku && Object.keys(availableOptions).length ? (
                  <div className="mt-4 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Choose your options</p>
                    {minimumQuantity > 1 && <p className="text-xs text-slate-600">Minimum order: {minimumQuantity} items</p>}
                    {Object.entries(availableOptions).map(([propName, valuesMap]) => (
                      <div key={propName}>
                        <p className="text-sm font-semibold text-slate-800 mb-2">{propName}</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.values(valuesMap).map((opt) => {
                            const active = selectedAttributes?.[propName] === opt.value;
                            const nextSku = selectSkuForOption(details?.skus, selectedAttributes, propName, opt.value);
                            return (
                              <button
                                type="button"
                                key={`${propName}:${opt.value}`}
                                disabled={!nextSku}
                                aria-pressed={active}
                                aria-label={propName + ": " + opt.value}
                                onClick={() => {
                                  if (nextSku) setSelectedAttributes(skuAttributes(nextSku));
                                }}
                                className={
                                  "px-3 py-1.5 rounded-xl border text-sm font-semibold transition-colors flex items-center gap-2 " +
                                  (active
                                    ? "border-[#D2042D] bg-[#D2042D]/10 text-[#D2042D]"
                                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300")
                                }
                              >
                                {opt?.image ? (
                                  <img
                                    src={opt.image}
                                    alt=""
                                    className={
                                      "w-6 h-6 rounded-md object-cover bg-white"
                                    }
                                    loading="lazy"
                                  />
                                ) : null}
                                <span>{opt.value}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <p className={`mt-4 text-sm font-medium ${stock > 0 ? "text-green-600" : "text-red-600"}`}>
                  {stock > 0 ? `In Stock (${stock})` : "Out of Stock"}
                </p>

                {descriptionLines.length ? (
                  <ul className="mt-3 text-slate-600 text-sm list-disc pl-5 space-y-1">
                    {descriptionLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : null}

                {/* Mobile actions at bottom */}
                <div className="mt-6 md:hidden">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={handleAddToCartClick}
                      className="flex-1 h-10 px-4 rounded-xl text-sm font-semibold bg-slate-900 text-white transition-colors hover:bg-[#D2042D] disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
                      disabled={!canPurchase || isAdding}
                    >
                      {isAdding ? "Adding…" : "Add to Cart"}
                    </button>
                    <button
                      onClick={handleBuyNowClick}
                      className="flex-1 h-10 px-4 rounded-xl text-sm font-semibold bg-[#D2042D] text-white transition-colors hover:bg-[#C1032A] disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
                      disabled={!canPurchase || isOrdering}
                    >
                      {isOrdering ? "Processing…" : "Buy Now"}
                    </button>
                  </div>

                  {productKey ? (
                    <Link
                      to={buildProductPath(
                        { publicId, productId, name },
                        name,
                        selectedSKU?.sku_id ? `sku=${encodeURIComponent(String(selectedSKU.sku_id))}` : ""
                      )}
                      onClick={onClose}
                      className="block mt-2"
                    >
                      <span className="w-full h-10 px-4 rounded-xl text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-center">View full product details</span>
                    </Link>
                  ) : null}
                </div>
                </div>
              </div>
            )}
          </div>
      </DialogPanel>
    </Dialog>
  );

  return modal;
};

export default QuickViewModal;
